import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output, Self } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, exhaustMap, retry, takeUntil, tap } from 'rxjs/operators';
import { AuthApiService } from '@core/services/api/auth.api.service';
import { AuthService } from '@core/services/auth-service.service';
import { TokenResponse } from '../../models/TokenResponse.model';
import { UnsubscribeService } from '../../services/unsubscribe.service';

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
	providers: [UnsubscribeService],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent implements OnInit {
	@Output() public closeDialog: EventEmitter<void> = new EventEmitter<void>();

	public loginForm: FormGroup;
	private submit$: Subject<boolean> = new Subject();

	constructor(
		private fb: FormBuilder,
		private authService: AuthService,
		private api: AuthApiService,
		private router: Router,
		@Self() private onDestroy$: UnsubscribeService,
	) { }

	public ngOnInit(): void {
		this.initForm();
		this.subscriptionToSubmit();
	}

	public submitForm(): void {
		this.submit$.next(true);
	}

	private initForm(): void {
		this.loginForm = this.fb.group({
			email: ['', [Validators.required]],
			password: ['', [Validators.required]]
		});
	}

	private subscriptionToSubmit(): void {
		this.submit$.pipe(
			exhaustMap(() => this.login()),
			retry(),
			takeUntil(this.onDestroy$),
		).subscribe((token: TokenResponse) => {
			this.authService.setToken(token);

			if (this.authService.routeBeforeGuard) {
				this.router.navigate([this.authService.routeBeforeGuard]);
				return;
			}
			this.router.navigate(['devconf']);
		});
	}

	private login(): Observable<TokenResponse> {
		const { email, password } = this.loginForm.controls;

		return this.api.login(email.value, password.value).pipe(
			tap(() => this.closeDialog.emit()),
			catchError((error: HttpErrorResponse) => {
				this.closeDialog.emit();
				return throwError(error);
			})
		);
	}
}
